import React, { useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ClockIcon, DocumentTextIcon } from "@heroicons/react/24/outline";
import { focusRing } from "../../Exams/TestBuilder/ui";

// The question page's tab is in its address, so a reload keeps it: none for the question (the default),
// ?tab=versions for its versions, with what is open there (?v=3, or ?from=2&to=5 for a comparison).
export const VERSIONS_TAB = "versions";
export const VERSION_PARAMS = ["v", "from", "to"];

export const questionTab = (searchParams) => (searchParams.get("tab") === VERSIONS_TAB ? VERSIONS_TAB : "question");

const withoutTab = (search) => {
  const params = new URLSearchParams(search);
  params.delete("tab");
  VERSION_PARAMS.forEach((key) => params.delete(key));
  const rest = params.toString();
  return rest ? `?${rest}` : "";
};

const withVersionsTab = (search) => {
  const params = new URLSearchParams(withoutTab(search));
  params.set("tab", VERSIONS_TAB);
  return `?${params.toString()}`;
};

/**
 * "Question | Versions" in the question page's top bar. Switching replaces the address (the page's Back still
 * leaves the page), and the Versions tab opens again on what it had open. 44px tall on phones; under 360px, where
 * they share a row with Back and "?", without their icons, so the labels stay whole.
 */
export default function QuestionTabs({ active, className = "" }) {
  const { t } = useTranslation();
  const { pathname, search } = useLocation();
  const lastVersions = useRef("");
  useEffect(() => {
    if (active === VERSIONS_TAB) lastVersions.current = search;
  }, [active, search]);

  const tabs = [
    { key: "question", label: t("question_versions.tabs.question"), Icon: DocumentTextIcon, search: withoutTab(search) },
    {
      key: VERSIONS_TAB,
      label: t("question_versions.tabs.versions"),
      Icon: ClockIcon,
      search: active === VERSIONS_TAB ? search : lastVersions.current || withVersionsTab(search),
    },
  ];

  return (
    <nav
      aria-label={t("question_versions.tabs.label")}
      className={`flex w-fit min-w-0 max-w-full items-center gap-1 rounded-xl bg-slate-100 p-1 ${className}`}
    >
      {tabs.map(({ key, label, Icon, search: to }) => {
        const current = key === active;
        return (
          <Link
            key={key}
            to={`${pathname}${to}`}
            replace
            aria-current={current ? "page" : undefined}
            className={`inline-flex h-11 min-w-0 items-center gap-2 rounded-lg px-3 text-sm font-medium transition duration-200 md:h-9 md:px-3.5 ${focusRing} ${
              current ? "bg-white text-slate-900 shadow-sm ring-1 ring-slate-200" : "text-slate-600 hover:bg-white/60 hover:text-slate-900"
            }`}
          >
            <Icon className={`h-4 w-4 shrink-0 max-[359px]:hidden ${current ? "text-blue-600" : "text-slate-400"}`} aria-hidden="true" />
            <span className="truncate">{label}</span>
          </Link>
        );
      })}
    </nav>
  );
}
